import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { HdContentLanguageService } from '../hd-content-language.service';
import { HD_COSMOLOGY_INFO_SECTIONS } from './hd-chart-basics-info-metadata';
import { HdChartBasicsInfoService } from './hd-chart-basics-info.service';
import { HdInfoSectionsComponent } from './hd-info-sections.component';
import { HdInfoSectionVm, HdInfoSectionsVm } from './hd-info-sections.models';

/**
 * Het paneel is bewust chart-onafhankelijk: het designmoment van de chart
 * hoort bij de gemaskeerde geboortegegevens (Geavanceerd → Toon) en wordt hier
 * dus niet herhaald.
 */
export type HdCosmologyInfoPanelData = Record<string, never>;

/**
 * Info-paneel "De drie dragers van bewustzijn", geopend vanaf de monopool- en
 * kristallenlaag op de bodygraph (Misc-tab → Bodygraph): voertuig, passagier
 * en bestuurder; de inprenting (conceptie, 88 graden, geboorte); de weg van
 * neutrino tot aura (base, tone, color, monopool); en de celkristallen.
 * Markdown onder `docs/human-design/cosmology/{nl,en}/`, labels in de scope
 * `human-design/chart-basics` (`cosmology.*`). Zelfde skelet als het
 * Basics-paneel ({@link HdInfoSectionsComponent}).
 */
@Component({
  selector: 'hd-cosmology-info-panel',
  standalone: true,
  imports: [HdInfoSectionsComponent],
  template: '<hd-info-sections [vm]="vm()" />',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdCosmologyInfoPanelComponent {
  private readonly info = inject(HdChartBasicsInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  protected readonly vm = computed<HdInfoSectionsVm>(() => {
    const lang = this.contentLang.effectiveLang();
    const t = (key: string) => this.info.text(`cosmology.${key}`, lang);
    const sections: HdInfoSectionVm[] = HD_COSMOLOGY_INFO_SECTIONS.map(key => ({
      key,
      label: t(`sections.${key}`),
      url: this.info.markdownUrl('cosmology', key, lang),
      isOwn: false,
      badges: [],
      facts: [],
    }));
    return {
      title: t('title'),
      strip: {
        label: t('stripLabel'),
        chips: sections.map(section => ({ sectionKey: section.key, label: section.label })),
      },
      sections,
    };
  });
}
